import { MenuItem, Pagination, TextField } from "@mui/material";
import { useAppDispatch } from "features/config/hooks";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";
import { formatTimeAgo } from "features/stock-ordering/presentation/components/stock-ordering-utils";
import { updateCommentReact } from "features/stock-ordering/presentation/slices/updateCommentReact.slice";
import { ChangeEventHandler, useState } from "react";
import { MaterialInput } from ".";
import moment from "moment";

interface CommentFieldProps {
  comment: string;
  setComment: (comment: string) => void;
  handlePost: () => void;
}

interface CommentData {
  comment: string;
  date: string;
  statusId: number;
  like: number;
  dislike: number;
  replyToId: number | null;
  commentId: number;
  firstName: string;
  lastName: string;
  replies: CommentData[];
}

interface CommentPreviewProps {
  reply: number | null;
  fieldComment: string;
  handleReply: (commentId: number | null) => void;
  handlePost: () => void;
  setComment: (comment: string) => void;
  commentInfo: {
    firstName: string;
    lastName: string;
    date: string;
    comment: string;
    commentId: number;
    replyToId: number | null;
    replies: CommentData[];
    like: number;
    dislike: number;
  };
}

interface CommentSectionProps {
  commentData: CommentData[];
  onCommentSubmit: (comment: string, reply: number | null) => void; // callback trigger dispatch
  currentPage?: number;
  commentMax?: number;
  commentPerPage: number;
  onRowsPerPageChange: ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  onPageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
}

export function CommentSection(props: CommentSectionProps) {
  const [comment, setComment] = useState<string>("");
  const [reply, setReply] = useState<number | null>(null);

  /**
   * Desctrucure props
   */
  const {
    commentData,
    commentMax = 25,
    currentPage = 1,
    onCommentSubmit,
    onRowsPerPageChange,
    commentPerPage,
    onPageChange,
  } = props ?? {
    commentData: [],
  };

  /**
   * Dispatches comment
   */
  const handlePost = () => {
    if (comment !== "") {
      onCommentSubmit(comment, reply);
      setComment("");
      setReply(null);
    }
  };

  return (
    <div className="flex flex-col space-y-2 font-[Roboto] text-xs shadow-sm rounded-md p-4 bg-white overflow-x-auto">
      <h2 className="text-lg font-semibold tracking-wider">Comments</h2>

      <CommentField
        comment={comment}
        setComment={setComment}
        handlePost={handlePost}
      />

      <hr />

      <div className="flex flex-col space-y-10">
        {commentData?.length === 0 ? (
          <p className="text-sm flex justify-center">
            Be the first one to comment
          </p>
        ) : (
          <div className={`flex flex-col px-2 space-y-2`}>
            {commentData?.map((rem) => (
              <CommentPreview
                key={rem.commentId}
                commentInfo={{
                  firstName: rem.firstName,
                  lastName: rem.lastName,
                  date: rem.date,
                  comment: rem.comment,
                  commentId: rem.commentId,
                  replyToId: rem.replyToId,
                  replies: rem.replies,
                  dislike: rem.dislike,
                  like: rem.like,
                }}
                reply={reply}
                handleReply={(comId: number | null) => setReply(comId)}
                fieldComment={comment}
                handlePost={handlePost}
                setComment={setComment}
              />
            ))}
          </div>
        )}
      </div>
      <div className="flex justify-end space-x-2 mt-2">
        <MaterialInput
          colorTheme="black"
          size="small"
          name="perPage"
          select
          value={commentPerPage.toString()}
          onChange={onRowsPerPageChange}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={25}>25</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
        </MaterialInput>
        <div className="flex self-center">
          <Pagination
            page={currentPage}
            onChange={onPageChange}
            count={Math.ceil(commentMax / commentPerPage)}
            variant="outlined"
            size="medium"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * @author knhrvs - kenoresca
 * ======================================================
 * Comment textfield / textarea
 * ======================================================
 *
 * max characters - 512
 * @param commentProps - CommentFieldProps
 * @returns Comment Field
 */
function CommentField(commentProps: CommentFieldProps) {
  const { comment, setComment, handlePost } = commentProps;

  /**
   * Handle textfield change or records textfield value for every text change
   * @param e - textfield change event
   */
  const handleCommentChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.value.length <= 512) {
      setComment(e.target.value);
    }
  };

  return (
    <div className="relative flex w-full">
      <TextField
        sx={{
          backgroundColor: "#F7F7F7",
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#BDBDBD",
            },
            "&:hover fieldset": {
              borderColor: "#757575",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#757575",
            },
          },
        }}
        InputProps={{
          style: {
            color: "#333333",
            paddingBottom: "40px",
          },
          endAdornment: (
            <div className="absolute right-2 bottom-2">
              <div className="flex space-x-3">
                <div className="flex text-xs self-end text-[#333333]">
                  <p>{comment.length}</p>
                  <p>/512</p>
                </div>
                <button
                  onClick={handlePost}
                  className="rounded-full text-sm tracking-wide bg-button text-white px-2 py-1"
                >
                  Comment
                </button>
              </div>
            </div>
          ),
        }}
        value={comment}
        onChange={handleCommentChange}
        name={"comment"}
        fullWidth
        multiline
      />
    </div>
  );
}

/**
 * @author knhrvs - kenoresca
 * ======================================================
 * COMMENT PREVIEW
 * ======================================================
 *
 * @param commentPreviewProps - CommentPreviewProps
 * @returns preview comments component
 */

function CommentPreview(commentPreviewProps: CommentPreviewProps) {
  const { firstName, lastName, date, comment, commentId, replies, replyToId } =
    commentPreviewProps.commentInfo;
  const { reply, fieldComment, handlePost, setComment, handleReply } =
    commentPreviewProps;

  const dispatch = useAppDispatch();

  /**
   * Handle comment with new line
   * @param com - Comment text
   * @returns - Formatted text with line breaks
   */
  const formatCommentPreview = (com: string) => {
    return (
      <>
        {com.split("\n").map((line, index) => (
          <p key={index}>{line}</p>
        ))}
      </>
    );
  };

  /**
   * to be implemented - not required
   * @param reactType string 'like' or 'dislike'
   */
  const handleReact = (reactType: string) => () => {
    dispatch(updateCommentReact({ commentId: commentId, type: reactType }));
  };

  return (
    <div className="flex space-x-3 space-y-1 mt-2">
      {/* Profile Image */}
      <img
        src={
          REACT_APP_DOMAIN_URL +
          "api/assets/images/shared/profile_defaults/MALE_ICON1.png"
        }
        alt="Profile"
        className="w-9 h-9 bg-button rounded-full"
      />

      {/* Comment Content */}
      <div className="w-full space-y-1">
        <div className="flex space-x-2">
          <span className="flex space-x-1 text-sm font-semibold capitalize">
            <p>{firstName}</p>
            <p>{lastName}</p>
          </span>

          {/* <p className="self-end">{formatTimeAgo(date)}</p> */}
          <p className="self-end">
            {moment(date).startOf("seconds").fromNow()}
          </p>
        </div>

        {/* Comment Body */}
        <div className="flex flex-col space-y-2">
          {formatCommentPreview(comment)}

          {/* Like, Dislike, Reply Buttons */}

          <div className="flex space-x-10 text-[#757575]">
            <button
              onClick={() =>
                reply === commentId ? handleReply(null) : handleReply(commentId)
              }
              className={`self-center font-semibold ${
                replyToId !== null && "hidden"
              }`}
            >
              Reply
            </button>
          </div>
          {replies && replies.length > 0 && (
            <div className="pl-10 border-l-2 border-gray-300">
              {replies.map((rep) => (
                <div className="flex flex-col space-y-2">
                  <CommentPreview
                    commentInfo={{
                      firstName: rep.firstName,
                      lastName: rep.lastName,
                      date: rep.date,
                      comment: rep.comment,
                      commentId: rep.commentId,
                      replyToId: rep.replyToId,
                      replies: rep.replies,
                      dislike: rep.dislike,
                      like: rep.like,
                    }}
                    reply={reply}
                    handleReply={(comId: number | null) => handleReply(comId)}
                    fieldComment={fieldComment}
                    handlePost={handlePost}
                    setComment={setComment}
                  />
                </div>
              ))}
            </div>
          )}

          {reply === commentId && (
            <CommentField
              comment={fieldComment}
              handlePost={handlePost}
              setComment={setComment}
            />
          )}
        </div>
      </div>
    </div>
  );
}
